import React, { useState, useRef, useEffect } from 'react';
import { Upload, CheckCircle2, AlertCircle, X, FileText, Camera } from 'lucide-react';
import { isNative, scanDocument, haptic } from '../../native';

import { compressPdfIfNeeded, formatSize, COMPRESS_THRESHOLD_BYTES } from '../../utils/pdfCompress';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,.doc,.xlsx,.xls,.csv';
const MAX_SIZE_MB = 200;

// Above this, the file goes straight to S3 instead of through the API.
//
// nginx sits in front of the server and caps a request body at 1 MB, so a
// statement larger than that was rejected at the proxy and never reached the
// application - measured, not assumed: 1000 KB got through and 1050 KB did
// not. Raising that cap is worth doing and is deployed separately, but it
// leaves large uploads dependent on a proxy setting. Sending the bytes
// directly to S3 removes the dependency: the request never passes through
// nginx, so there is no body limit to hit.
//
// Set below the measured ceiling with room to spare, because the limit applies
// to the whole multipart body - the file plus its form fields - not the file
// alone.
const DIRECT_TO_S3_ABOVE_BYTES = 700 * 1024;

// What the server records as fileSize. Matches the format /upload writes so
// that both routes produce identical records.
function serverFileSize(bytes) {
  return bytes >= 102400
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

// Presign, PUT to S3, then tell the server the file has landed.
//
// The Content-Type has to be the one the URL was signed for, or S3 rejects the
// PUT with a signature mismatch, so it is worked out once and used for both.
async function uploadDirectToS3(file, category, clientId, uploadedBy, bankAccount, onStage) {
  const contentType = file.type || 'application/octet-stream';

  const presign = await fetch(`${API}/documents/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId, category, fileName: file.name, contentType }),
  });
  if (!presign.ok) {
    const e = await presign.json().catch(() => ({}));
    throw new Error(e.error || `Could not start the upload (${presign.status})`);
  }
  const { url, key } = await presign.json();

  onStage && onStage('sending');
  const put = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
  if (!put.ok) throw new Error(`Upload to storage failed (${put.status})`);

  // Until this returns, the file is in S3 but the platform does not know about
  // it, so a failure here is worth surfacing rather than swallowing.
  const confirm = await fetch(`${API}/documents/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      key, clientId, category,
      fileName: file.name,
      fileSize: serverFileSize(file.size),
      uploadedBy: uploadedBy || 'unknown',
      bankAccount: bankAccount || undefined,
    }),
  });
  if (!confirm.ok) {
    const e = await confirm.json().catch(() => ({}));
    throw new Error(e.error || `Upload saved but could not be recorded (${confirm.status})`);
  }
  const data = await confirm.json();
  return data.doc?.key || key;
}

async function uploadToS3(file, category, clientId, uploadedBy, token, bankAccount) {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('clientId', clientId);
  formData.append('category', category);
  formData.append('uploadedBy', uploadedBy || 'unknown');
  if (bankAccount) formData.append('bankAccount', bankAccount);

  const res = await fetch(`${API}/documents/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.error || `Upload failed (${res.status})`;
    // Surface specific human-readable errors
    if (res.status === 413 || msg.toLowerCase().includes('too large') || msg.toLowerCase().includes('file size')) {
      // Reaching this now means the file really is enormous. Anything merely
      // large was already shrunk before it was sent, so telling the rep to go
      // and compress it by hand would be advice we have already acted on.
      throw new Error(`"${file.name}" is ${formatSize(file.size)}, which is over the ${MAX_SIZE_MB}MB limit even after compression. Please split it into separate files - one per statement period - and upload those.`);
    }
    if (res.status === 415 || msg.toLowerCase().includes('file type') || msg.toLowerCase().includes('unsupported')) {
      throw new Error(`Unsupported file type. Please upload PDF, JPG, PNG, HEIC, DOCX, XLSX, or CSV only.`);
    }
    if (msg.toLowerCase().includes('too many')) {
      throw new Error(`Too many files at once. Try uploading one at a time or in smaller batches.`);
    }
    throw new Error(msg);
  }

  const data = await res.json();
  return data.docs?.[0]?.key || 'uploaded';
}

export default function UploadZone({
  category,
  categoryLabel,
  clientId,
  uploadedBy,
  token,
  onUpload,
  compact = false,
  // A compact zone with more height - a bigger target to drop files on,
  // without the extra lines of text the full-size zone carries.
  tall = false,
  defaultBankAccount = null,
}) {
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [bankAccount, setBankAccount] = useState(defaultBankAccount || 'Account 1');
  const inputRef = useRef();

  // Sync with parent's selected account
  useEffect(() => {
    if (defaultBankAccount) setBankAccount(defaultBankAccount);
  }, [defaultBankAccount]);

  // A patch value may be a function of the previous value, which is how the
  // progress ticker advances the bar without reading state it does not have.
  // Passing one used to store the function itself, so `width: ${u.progress}%`
  // was never a number and the bar sat at zero for the whole upload.
  const updateUpload = (id, patch) =>
    setUploads(prev => prev.map(u => {
      if (u.id !== id) return u;
      const next = { ...u };
      for (const [k, v] of Object.entries(patch)) next[k] = typeof v === 'function' ? v(u[k]) : v;
      return next;
    }));

  const handleFiles = async (files) => {
    const list = Array.from(files);

    for (const file of list) {
      // Validate size
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setUploads(prev => [...prev, { id: `${Date.now()}_${file.name}`, name: file.name, status: 'error', error: `"${file.name}" is too large (max ${MAX_SIZE_MB}MB). For bank statements, use ilovepdf.com to compress it first.` }]);
        continue;
      }

      const id = `${Date.now()}_${file.name}`;
      setUploads(prev => [...prev, { id, name: file.name, size: file.size, status: 'uploading', progress: 0 }]);

      try {
        // Shrink it first, if it needs it.
        //
        // Files under the threshold are untouched - the overwhelming majority
        // of what comes through is well under a megabyte and there is nothing
        // to gain. A large scan is re-saved, and rasterised only if that is not
        // enough, which is what turns an unusable 40 MB statement into one that
        // uploads and can actually be submitted.
        //
        // Deliberately sequential: this loop awaits each file before starting
        // the next, so several large statements dropped together are worked
        // through one at a time rather than competing for memory and stalling
        // the tab.
        let toUpload = file;
        if (file.size > COMPRESS_THRESHOLD_BYTES) {
          updateUpload(id, { status: 'compressing', progress: 5, note: 'Compressing…' });
          const result = await compressPdfIfNeeded(file, (stage) => {
            updateUpload(id, {
              note: stage === 'resaving' ? 'Compressing…' : 'Compressing (reducing image quality)…',
            });
          });
          toUpload = result.file;
          if (result.compressed) {
            updateUpload(id, {
              status: 'uploading',
              progress: 10,
              note: `Compressed ${formatSize(result.originalSize)} → ${formatSize(result.finalSize)}`,
            });
          } else {
            // Could not be compressed - encrypted, or already as small as it
            // gets. Upload it anyway rather than refusing outright.
            updateUpload(id, { status: 'uploading', progress: 10, note: null });
          }
        }

        // Simulate progress while uploading (XHR would give real progress but fetch doesn't)
        const progressInterval = setInterval(() => {
          updateUpload(id, { progress: p => Math.min((p || 0) + 15, 85) });
        }, 300);

        // Big files bypass the API and go straight to S3; small ones keep the
        // path they have always taken.
        const acct = category === 'bank_statements' ? bankAccount : null;
        if (toUpload.size > DIRECT_TO_S3_ABOVE_BYTES) {
          await uploadDirectToS3(toUpload, category, clientId, uploadedBy, acct, () =>
            updateUpload(id, { progress: 40 })
          );
        } else {
          await uploadToS3(toUpload, category, clientId, uploadedBy, token, acct);
        }

        clearInterval(progressInterval);
        updateUpload(id, { status: 'done', progress: 100 });
        haptic('success');

        if (onUpload) onUpload(category, [toUpload]);
      } catch (err) {
        updateUpload(id, { status: 'error', error: err.message });
        haptic('error');
        console.error('[UploadZone]', err.message);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeUpload = (id) => setUploads(prev => prev.filter(u => u.id !== id));

  // Native app only: photograph a paper document (or pick a photo) and upload it
  // through the same path as a browsed file.
  const onScan = async (e) => {
    e.stopPropagation();
    haptic();
    try {
      const file = await scanDocument();
      if (file) handleFiles([file]);
    } catch (err) {
      setUploads(prev => [...prev, { id: `${Date.now()}_scan`, name: 'Camera', status: 'error', error: err.message || 'Could not open the camera.' }]);
    }
  };

  const zone = (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-xl cursor-pointer transition-all text-center select-none ${
        dragging
          ? 'border-blue-500 bg-blue-50 scale-[1.01]'
          : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50'
      } ${compact ? (tall ? 'px-4 py-12' : 'p-4') : 'p-10'}`}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED}
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />
      <Upload
        size={compact ? (tall ? 24 : 18) : 26}
        className={`mx-auto mb-2 transition-colors ${dragging ? 'text-blue-500' : 'text-gray-400'}`}
      />
      {!compact && (
        <p className="text-gray-700 font-medium text-sm mb-0.5">{categoryLabel}</p>
      )}
      <p className={`text-gray-400 ${tall ? 'text-sm' : 'text-xs'}`}>
        Drop files here or <span className="text-blue-500 font-medium">browse</span>
      </p>
      {!compact && (
        <p className="text-gray-300 text-xs mt-1">PDF Â· JPG Â· PNG Â· DOCX Â· Max {MAX_SIZE_MB}MB Â· Encrypted in S3</p>
      )}
    </div>
  );

  return (
    <div className="space-y-2">
      {category === 'bank_statements' && !compact && !defaultBankAccount && (
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-medium text-gray-500">Bank Account:</label>
          <select value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {['Account 1', 'Account 2', 'Account 3', 'Account 4', 'Account 5'].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      )}
      {zone}
      {isNative && (
        <button
          type="button"
          onClick={onScan}
          className={`w-full flex items-center justify-center gap-2 rounded-xl border border-blue-100 bg-blue-50 text-blue-700 font-medium active:bg-blue-100 transition-colors ${compact ? 'py-2 text-xs' : 'py-3 text-sm'}`}
        >
          <Camera size={compact ? 14 : 16} />
          Scan document with camera
        </button>
      )}

      {/* Upload status list */}
      {uploads.map(u => (
        <div
          key={u.id}
          className={`flex items-center gap-3 text-xs px-3 py-2.5 rounded-xl border transition-all ${
            u.status === 'done'
              ? 'bg-green-50 border-green-100'
              : u.status === 'error'
              ? 'bg-red-50 border-red-100'
              : 'bg-blue-50 border-blue-100'
          }`}
        >
          {/* Icon */}
          <div className="shrink-0">
            {u.status === 'done' && <CheckCircle2 size={14} className="text-green-600" />}
            {u.status === 'error' && <AlertCircle size={14} className="text-red-500" />}
            {(u.status === 'uploading' || u.status === 'compressing') && (
              <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FileText size={11} className="text-gray-400 shrink-0" />
              <span className={`font-medium truncate ${
                u.status === 'done' ? 'text-green-700' :
                u.status === 'error' ? 'text-red-600' : 'text-blue-700'
              }`}>{u.name}</span>
            </div>

            {(u.status === 'uploading' || u.status === 'compressing') && (
              <div className="mt-1.5">
                <div className="w-full bg-blue-100 rounded-full h-1">
                  <div
                    className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${u.progress || 0}%` }}
                  />
                </div>
                {/* Says what is actually happening. A large scan takes a while
                    to compress, and a bar reading "uploading" while nothing is
                    uploading looks like a hang. */}
                <p className="text-blue-400 text-xs mt-0.5">
                  {u.status === 'compressing'
                    ? (u.note || 'Compressing…')
                    : `Uploading to S3... ${u.progress || 0}%`}
                </p>
                {u.status === 'uploading' && u.note && (
                  <p className="text-gray-400 text-[10px] mt-0.5">{u.note}</p>
                )}
              </div>
            )}

            {u.status === 'done' && (
              <p className="text-green-500 text-xs mt-0.5">âœ“ Saved to S3 Â· {u.size ? (u.size < 102400 ? `${Math.round(u.size/1024)} KB` : `${(u.size/1024/1024).toFixed(1)} MB`) : ''}</p>
            )}

            {u.status === 'error' && (
              <p className="text-red-400 text-xs mt-0.5">{u.error}</p>
            )}
          </div>

          {/* Remove button */}
          {u.status !== 'uploading' && (
            <button
              onClick={() => removeUpload(u.id)}
              className="shrink-0 p-1 hover:bg-white/60 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
